import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  setCsrfToken,
  setUnauthorisedHandler,
  type AccountSummary,
  type Session,
} from "@/api/client";
interface AuthState {
  account: AccountSummary | null;
  checking: boolean;
  complete: (session: Session) => void;
  signOut: () => Promise<void>;
  invalidate: () => void;
}
const AuthContext = createContext<AuthState | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [checking, setChecking] = useState(true);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const complete = useCallback((session: Session) => {
    if (!session.user.is_super_admin) throw new Error("not an admin");
    setCsrfToken(session.csrf_token);
    setExpiresAt(session.expires_at);
    setAccount(session.user);
  }, []);
  useEffect(() => {
    setUnauthorisedHandler(() => {
      setCsrfToken(null);
      setExpiresAt(null);
      setAccount(null);
    });
    let cancelled = false;
    api
      .me()
      .then((session) => {
        if (!cancelled) complete(session);
      })
      .catch(() => {
        if (!cancelled) {
          setCsrfToken(null);
          setExpiresAt(null);
          setAccount(null);
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [complete]);
  useEffect(() => {
    if (!expiresAt) return;
    const delay = Math.max(0, new Date(expiresAt).getTime() - Date.now());
    const timeout = window.setTimeout(() => {
      setCsrfToken(null);
      setAccount(null);
      setExpiresAt(null);
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [expiresAt]);
  const invalidate = useCallback(() => {
    setCsrfToken(null);
    setExpiresAt(null);
    setAccount(null);
  }, []);
  const signOut = useCallback(async () => {
    await api.logout();
    setCsrfToken(null);
    setExpiresAt(null);
    setAccount(null);
  }, []);
  const value = useMemo(
    () => ({ account, checking, complete, signOut, invalidate }),
    [account, checking, complete, signOut, invalidate],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth requires AuthProvider");
  return context;
}
